import { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import { Button, Tooltip, Chip } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import PlusIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
} from "@dnd-kit/modifiers";

import { Loading } from "../../../sharedComponents/Loading";
import { useFetchSections } from "../../../../hooks/useGetSections";
import { useFetchNotas, fetchNotasDesdeAPI } from "../../../../hooks/useGetNotes";
import { useUpdateSingleNoteOrder } from "../../../../hooks/panelClient/useUpdateSingleNoteOrder";

import useNoteEditionToSection from "../../../../hooks/useNoteEditionToSection";
import useNotesToSection from "../../../../hooks/useNotesToSection";
import DeleteMultipleNotesModal from "./TabSortNotes/modals/DeleteMultipleNotesModal";
import EditNoteModal from "./TabSortNotes/modals/EditNoteModal";
import ModalAddNoteWithEdition from "./TabSortNotes/modals/AddNoteWithEditionModal";
import ModalDeleteSection from "./TabSortNotes/modals/DeleteSectionModal";
import SortableNoteItem from "./TabSortNotes/SortTableNoteItem";
import DateSelector from "./TabSortNotes/DateSelector";
import { useFilter } from "../../../../context/FilterContext";

moment.locale("es");

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const SectionTitleWithCount = ({ title, count }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      overflow: "hidden",
      maxWidth: "calc(100% - 40px)",
    }}
  >
    <span
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </span>
    <Chip
      label={count || 0}
      size="small"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.22)", color: "inherit" }} 
      sx={{
        height: "22px",
        fontWeight: "bold",
        "& .MuiChip-label": { padding: "0 8px", fontSize: "0.75rem" },
      }}
    />
  </div>
);

export const TabSortNotes = ({
  token,
  idDocument,
  notaSeleccionada,
  notasSeleccionadas,
  selectedDate,
  onDateChange,
}) => {
  const { editedNotaData, avanzarSeleccion } = useFilter();
  const [expanded, setExpanded] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const { sections, loading } = useFetchSections(token, idDocument, refresh);
  const [editModalNote, setEditModalNote] = useState(false);
  const [deleteModalSection, setDeleteModalSection] = useState(false);
  const [deleteMultipleModalOpen, setDeleteMultipleModalOpen] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);

  const fechaTransferencia = `${selectedDate} 12:00:00`;

  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [orderedNotas, setOrderedNotas] = useState([]);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const { updateSingleNoteOrder } = useUpdateSingleNoteOrder(token);

  const { notas, loadingNote } = useFetchNotas(
    token,
    selectedDate,
    expanded,
    refresh,
    idDocument,
  );

  const [sectionCounts, setSectionCounts] = useState({});

  useEffect(() => {
    const fetchAllCounts = async () => {
      if (!sections || sections.length === 0 || !selectedDate || !idDocument) return;

      const newCounts = {};
      
      await Promise.all(
        sections.map(async (section) => {
          try {
            const data = await fetchNotasDesdeAPI(
              token,
              selectedDate,
              section.ID_SECCION,
              idDocument
            );
            newCounts[section.ID_SECCION] = data ? data.length : 0;
          } catch (error) {
            console.error(`Error al obtener notas para la sección ${section.ID_SECCION}:`, error);
            newCounts[section.ID_SECCION] = 0;
          }
        })
      );
      
      setSectionCounts(newCounts);
    };

    fetchAllCounts();
  }, [sections, token, selectedDate, idDocument, refresh]);

  useEffect(() => {
    const sorted = [...notas]
      .map((item, index) => {
        const orden = item.orden_editor ?? item.ORDEN_EDITOR ?? index;
        return {
          ...item,
          orden_editor: orden,
        };
      })
      .sort((a, b) => a.orden_editor - b.orden_editor);
    setOrderedNotas(sorted);
  }, [notas]);

  const toggleSection = (idSection) => {
    setExpanded((prev) => (prev === idSection ? null : idSection));
  };

  const styleButtons = {
    padding: "6px",
    fontSize: "inherit",
    minWidth: "auto",
    marginLeft: "5px",
  };

  const { addNoteWithEditionToSection, isAdding: isAddingWithEdition } =
    useNoteEditionToSection();
  const { addNotesToSection, isAdding: isAddingMultiple } = useNotesToSection();

  const handleAddNoteToSection = async (section) => {
    if (!notaSeleccionada) {
      alert(
        "Por favor, selecciona una nota en la sección de edición antes de agregarla a una sección.",
      );
      return;
    }
    
    const capclave = notaSeleccionada.CAPCLAVE || notaSeleccionada.capclave;
    const titulo = stripHtml(editedNotaData?.titulo || notaSeleccionada.CAPTITULO || "");
    const sintesis = stripHtml(editedNotaData?.contenido || notaSeleccionada.CAPTEXTCOMP || "");
    
    if (!titulo.trim()) {
      alert("El título es requerido.");
      return;
    }

    avanzarSeleccion();

    try {
      const result = await addNoteWithEditionToSection(
        idDocument,
        section.ID_SECCION,
        capclave,
        titulo,
        sintesis,
        token,
        fechaTransferencia,
      );

      if (result.success) {
        setRefresh(!refresh);
      }
    } catch (error) {
      console.error("Error al agregar nota a la sección:", error);
    }
  };

  const handleAddMultipleNotesToSection = async (section) => {
    if (!notasSeleccionadas || notasSeleccionadas.length === 0) {
      alert(
        "Por favor, selecciona notas en la tabla de notas antes de agregarlas a una sección.",
      );
      return;
    }

    const cvecapsulas = notasSeleccionadas
      .map((nota) => {
        const capclave = nota.CAPCLAVE || nota.capclave;
        if (!capclave) {
          console.error("Nota sin CAPCLAVE:", nota);
          return null;
        }
        return capclave.toString();
      })
      .filter(Boolean);

    if (cvecapsulas.length === 0) {
      alert(
        "Las notas seleccionadas no tienen identificadores válidos (CAPCLAVE).",
      );
      return;
    }

    try {
      const result = await addNotesToSection(
        idDocument,
        section.ID_SECCION,
        cvecapsulas,
        token,
        fechaTransferencia,
      );

      if (result.success) {
        setRefresh(!refresh);
      }
    } catch (error) {
      console.error("Error al agregar múltiples notas a la sección:", error);
    }
  };

  const handleOpenDeleteMultiple = (section) => {
    setSelectedSection(section);
    setDeleteMultipleModalOpen(true);
  };

  const handleDropOnSection = async (e, section) => {
    e.preventDefault();
    setDragOverSectionId(null);
    try {
      const dataStr = e.dataTransfer.getData("application/json");
      if (!dataStr) return;
      const data = JSON.parse(dataStr);
      if (data.type === "NOTES_DRAG" && data.ids && data.ids.length > 0) {
        if (data.ids.length === 1) {
          avanzarSeleccion();
        }

        const cvecapsulas = data.ids.map((id) => id.toString());
        const result = await addNotesToSection(
          idDocument,
          section.ID_SECCION,
          cvecapsulas,
          token,
          fechaTransferencia,
        );
        if (result.success) {
          setRefresh(!refresh);
        }
      }
    } catch (err) {
      console.error("Error procesando drop de notas:", err);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedNotas.findIndex(
      (item) => item.id_sintesis === active.id,
    );
    const newIndex = orderedNotas.findIndex(
      (item) => item.id_sintesis === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(orderedNotas, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        orden_editor: index,
      }),
    );

    setOrderedNotas(reordered);

    const ids = reordered.map((item) => item.id_sintesis);
    
    const idSeccion = expanded;
    
    if (idSeccion && idDocument) {
      try {
        await updateSingleNoteOrder(idSeccion, idDocument, ids);
      } catch (error) {
        console.error("Error al reordenar las notas en la sección:", error);
      }
    } else {
      console.error("Faltan idSeccion o idDocument para reordenar en lote:", { idSeccion, idDocument });
    }
  };

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        paddingRight: "8px",
      }}
    >
      <DateSelector
        selectedDate={selectedDate}
        onDateChange={onDateChange}
      />
      {loading ? (
        <Loading />
      ) : (
        <>
          <div
            style={{
              padding: "10px",
              marginBottom: "10px",
              backgroundColor: notaSeleccionada ? "#e8f5e8" : "#f5f5f5",
              border: `1px solid ${notaSeleccionada ? "#4caf50" : "#ddd"}`,
              borderRadius: "4px",
              fontSize: "14px",
            }}
          >
            {notaSeleccionada ? (
              <div>
                <strong>Nota seleccionada:</strong> {notaSeleccionada.titulo}
                <br />
                <small style={{ color: "#666" }}>
                  Haz clic en el botón "+" de cualquier sección para agregar
                  esta nota con edición
                </small>
              </div>
            ) : (
              <div style={{ color: "#666" }}>
                <strong>No hay nota seleccionada</strong>
                <br />
                <small>
                  Selecciona una nota en la sección de edición para poder
                  agregarla a las secciones
                </small>
              </div>
            )}
          </div>

          {notasSeleccionadas && notasSeleccionadas.length > 0 && (
            <div
              style={{
                padding: "10px",
                marginBottom: "10px",
                backgroundColor: "#e3f2fd",
                border: "1px solid #2196f3",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              <div>
                <strong>
                  {notasSeleccionadas.length} notas seleccionadas para agregar
                  en lote:
                </strong>
                <br />
                <small style={{ color: "#666" }}>
                  {notasSeleccionadas
                    .slice(0, 3)
                    .map((nota) => nota.CAPTITULO?.substring(0, 40) + "...")
                    .join(", ")}
                  {notasSeleccionadas.length > 3 &&
                    ` y ${notasSeleccionadas.length - 3} más`}
                </small>
                <br />
                <small style={{ color: "#1976d2", fontWeight: "600" }}>
                  Haz clic en el botón "+" de cualquier sección para agregar
                  todas estas notas
                </small>
              </div>
            </div>
          )}

          <div style={{ paddingBottom: "20px" }}>
            {sections.map((section, index) => (
              <div
                key={index}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragOverSectionId(section.ID_SECCION);
                }}
                onDragLeave={() => setDragOverSectionId(null)}
                onDrop={(e) => handleDropOnSection(e, section)}
                style={{
                  backgroundColor:
                    dragOverSectionId === section.ID_SECCION
                      ? "rgba(33, 150, 243, 0.1)"
                      : "transparent",
                  border:
                    dragOverSectionId === section.ID_SECCION
                      ? "2px dashed #2196f3"
                      : "2px solid transparent",
                  borderRadius: "8px",
                  padding: "4px",
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <Button
                    variant="contained"
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      textAlign: "left",
                    }}
                    onClick={() => toggleSection(section.ID_SECCION)}
                  >
                    <SectionTitleWithCount 
                      title={section.NOMBRE_SECCION} 
                      count={sectionCounts[section.ID_SECCION] !== undefined ? sectionCounts[section.ID_SECCION] : "..."} 
                    />
                    <ExpandMoreIcon
                      style={{
                        transform:
                          expanded === section.ID_SECCION
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </Button>

                  <Tooltip
                    title={
                      notaSeleccionada
                        ? `Agregar nota "${notaSeleccionada.titulo}" a esta sección con edición`
                        : "Selecciona una nota en la sección de edición para agregar"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        style={{ ...styleButtons }}
                        onClick={() => handleAddNoteToSection(section)}
                        disabled={isAddingWithEdition || !notaSeleccionada}
                      >
                        <PlusIcon />
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      notasSeleccionadas && notasSeleccionadas.length > 0
                        ? `Agregar ${notasSeleccionadas.length} notas seleccionadas a esta sección`
                        : "Selecciona múltiples notas en la tabla para agregar en lote"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        style={{
                          ...styleButtons,
                          backgroundColor:
                            notasSeleccionadas && notasSeleccionadas.length > 0
                              ? "#2196f3"
                              : undefined,
                          color:
                            notasSeleccionadas && notasSeleccionadas.length > 0
                              ? "white"
                              : undefined,
                        }}
                        onClick={() => handleAddMultipleNotesToSection(section)}
                        disabled={
                          isAddingMultiple ||
                          !notasSeleccionadas ||
                          notasSeleccionadas.length === 0
                        }
                      >
                        <PlaylistAddIcon />
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip title="Eliminación múltiple de notas en esta sección">
                    <span>
                      <Button
                        variant="contained"
                        style={{
                          ...styleButtons,
                          backgroundColor: "#f44336",
                          color: "white",
                        }}
                        onClick={() => handleOpenDeleteMultiple(section)}
                      >
                        <DeleteIcon />
                      </Button>
                    </span>
                  </Tooltip>
                </div>

                {expanded === section.ID_SECCION && (
                  <div style={{ padding: "10px", background: "#f9f9f9" }}>
                    {loadingNote ? (
                      <Loading message="Cargando Notas..." />
                    ) : orderedNotas.length === 0 ? (
                      <p
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      >
                        No hay datos disponibles
                      </p>
                    ) : (
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[
                          restrictToVerticalAxis,
                          restrictToWindowEdges,
                          restrictToFirstScrollableAncestor,
                          restrictToParentElement,
                        ]}
                      >
                        <SortableContext
                          items={orderedNotas.map((item) => item.id_sintesis)}
                          strategy={verticalListSortingStrategy}
                        >
                          {orderedNotas.map((nota) => (
                            <SortableNoteItem
                              key={nota.id_sintesis}
                              nota={nota}
                              token={token}
                              idDocument={idDocument}
                              idSeccion={section.ID_SECCION}
                              onEdit={() => {
                                setEditModalNote(true);
                                setSelectedNote(nota);
                              }}
                              refresh={() => setRefresh((prev) => !prev)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <ModalAddNoteWithEdition
            open={addNoteModal}
            close={() => setAddNoteModal(false)}
            section={selectedSection}
            note={notaSeleccionada}
            idDocument={idDocument}
            token={token}
            refresh={() => setRefresh(!refresh)}
            addNoteWithEditionToSection={addNoteWithEditionToSection}
            isAddingWithEdition={isAddingWithEdition}
            selectedDate={fechaTransferencia}
          />

          <EditNoteModal
            open={editModalNote}
            close={() => setEditModalNote(!editModalNote)}
            note={selectedNote}
            refresh={() => setRefresh(!refresh)}
            token={token}
            idDocument={idDocument}
          />

          <ModalDeleteSection
            open={deleteModalSection}
            close={() => setDeleteModalSection(!deleteModalSection)}
            section={selectedSection}
            refresh={() => setRefresh(!refresh)}
            token={token}
          />

          <DeleteMultipleNotesModal
            open={deleteMultipleModalOpen}
            onClose={() => setDeleteMultipleModalOpen(false)}
            section={selectedSection}
            token={token}
            idDocument={idDocument}
            selectedDate={selectedDate}
            onDeleteSuccess={() => setRefresh((prev) => !prev)}
          />
        </>
      )}
    </div>
  );
};
